import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import client from "@/api/client";
import { KYC_ACCEPT, uploadVerificationDocument } from "@/lib/verificationDocs";

const initialForm = { shopName: "", ownerName: "", ownerEmail: "", ownerPassword: "", phone: "", city: "", gstNumber: "", address: "" };

export default function RegisterShopPage() {
  const { registerShop, login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [documents, setDocuments] = useState([]);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await registerShop(form);
      // Documents are uploaded as the new owner, so sign in first. A failure here leaves the shop
      // registered; the documents can be added again from Shop Verification.
      await login(form.ownerEmail, form.ownerPassword);
      if (documents.length) {
        try {
          const ids = [];
          for (const file of documents) ids.push(await uploadVerificationDocument(file));
          await client.patch("/tenants/me", { verificationFileIds: ids });
        } catch {
          toast.error("Shop registered, but the documents didn't upload. Add them from Shop Verification.");
        }
      }
      setDone(true);
      toast.success("Shop registered! Awaiting Platform Admin approval.");
    } catch (err) {
      toast.error(err?.response?.data?.error?.message || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF8F5] px-4">
        <div className="w-full max-w-md bg-white border border-[#E5DEC9] rounded-2xl shadow-sm p-10 text-center" data-testid="register-success-message">
          <p className="font-heading text-2xl font-bold text-[#2A211D]">Shop submitted!</p>
          <p className="text-sm text-[#6E6760] mt-3">
            Your shop is <strong>PENDING_VERIFICATION</strong>. A Platform Admin will review it shortly.
            You're signed in. Most actions unlock after approval; you can add or change verification documents meanwhile.
          </p>
          <Button className="mt-6 w-full bg-[#C85A32] hover:bg-[#B04B26] text-white" onClick={() => navigate("/shop-verification")} data-testid="register-go-to-verification-button">
            Continue
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAF8F5] px-4 py-10">
      <div className="w-full max-w-lg bg-white border border-[#E5DEC9] rounded-2xl shadow-sm p-8 sm:p-10">
        <p className="font-heading text-3xl font-extrabold text-[#2A211D] tracking-tight">Register your Shop</p>
        <p className="text-sm text-[#6E6760] mt-2 mb-8">
          Your shop starts as PENDING_VERIFICATION until a Platform Admin approves it.
        </p>
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="register-shop-form">
          <div>
            <Label>Shop Name</Label>
            <Input data-testid="register-shopname-input" required value={form.shopName} onChange={update("shopName")} className="mt-1.5" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Owner Name</Label>
              <Input data-testid="register-ownername-input" required value={form.ownerName} onChange={update("ownerName")} className="mt-1.5" />
            </div>
            <div>
              <Label>City</Label>
              <Input data-testid="register-city-input" value={form.city} onChange={update("city")} className="mt-1.5" />
            </div>
          </div>
          <div>
            <Label>Owner Email</Label>
            <Input data-testid="register-owneremail-input" type="email" required value={form.ownerEmail} onChange={update("ownerEmail")} className="mt-1.5" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Password</Label>
              <Input data-testid="register-password-input" type="password" required minLength={8} value={form.ownerPassword} onChange={update("ownerPassword")} className="mt-1.5" />
            </div>
            <div>
              <Label>Phone</Label>
              <Input data-testid="register-phone-input" value={form.phone} onChange={update("phone")} className="mt-1.5" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>GST Number</Label>
              <Input data-testid="register-gst-input" value={form.gstNumber} onChange={update("gstNumber")} className="mt-1.5" placeholder="e.g. 29ABCDE1234F1Z5" />
            </div>
            <div>
              <Label>Shop Address</Label>
              <Input data-testid="register-address-input" value={form.address} onChange={update("address")} className="mt-1.5" />
            </div>
          </div>
          <div>
            <Label>Verification Documents</Label>
            <Input
              data-testid="register-documents-input"
              type="file"
              multiple
              accept={KYC_ACCEPT}
              onChange={(e) => setDocuments(Array.from(e.target.files || []))}
              className="mt-1.5"
            />
            <p className="text-xs text-[#8C8379] mt-1">GST certificate or shop proof (PDF, PNG or JPEG). You can also add these later.</p>
          </div>
          <Button type="submit" disabled={loading} data-testid="register-submit-button" className="w-full bg-[#C85A32] hover:bg-[#B04B26] text-white mt-2">
            {loading ? "Submitting..." : "Register Shop"}
          </Button>
        </form>
        <p className="text-sm text-[#6E6760] mt-6 text-center">
          Already registered?{" "}
          <Link to="/login" data-testid="go-to-login-link" className="text-[#C85A32] font-medium hover:underline">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
